/* Padel USA Partner Program page (sections/partners_custom.liquid).
   Handles: door selection -> track-specific fields, earnings calculator, JSON submit
   to the registration endpoint (which registers into UpPromote by track).
   Contract notes live in the section's header comment — keep data-* hooks in sync. */
(function () {
  'use strict';

  var root = document.querySelector('[data-partners]');
  if (!root) return;

  /* Scroll reveals: CSS handles the transition (gated behind prefers-reduced-motion);
     JS only flips .is-in as elements enter the viewport. */
  var revealEls = root.querySelectorAll('[data-reveal]');
  if ('IntersectionObserver' in window && revealEls.length) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
      });
    }, { threshold: 0.2, rootMargin: '0px 0px -5% 0px' });
    revealEls.forEach(function (el) { io.observe(el); });
  } else {
    revealEls.forEach(function (el) { el.classList.add('is-in'); });
  }

  var endpoint = root.getAttribute('data-endpoint') || '';
  var form = root.querySelector('[data-partners-form]');
  var trackRadios = form ? Array.prototype.slice.call(form.querySelectorAll('input[name="track"]')) : [];
  var doors = Array.prototype.slice.call(root.querySelectorAll('[data-door]'));
  var currentTrack = '';

  /* ---------- Track selection (door tiles <-> form radio pills, kept in sync) ---------- */
  function selectTrack(track, doScroll) {
    currentTrack = track;
    trackRadios.forEach(function (r) { r.checked = r.value === track; });
    doors.forEach(function (d) {
      var on = d.getAttribute('data-door') === track;
      d.classList.toggle('is-selected', on);
      d.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    // Toggle track-specific fields. Hidden fields are also disabled so a
    // `required` question for another track can't block validation or submit data.
    if (form) {
      Array.prototype.forEach.call(form.querySelectorAll('[data-fields]'), function (el) {
        var show = el.getAttribute('data-fields').split(' ').indexOf(track) !== -1;
        el.hidden = !show;
        var input = el.querySelector('input, select, textarea');
        if (input) input.disabled = !show;
      });
    }
    // Swap intro copy for the high-touch club track
    var intro = root.querySelector('[data-form-intro]');
    if (intro) {
      intro.textContent = track === 'club'
        ? intro.getAttribute('data-intro-club')
        : intro.getAttribute('data-intro-default');
    }
    setCalcRole(track, true);
    if (doScroll) {
      var apply = document.getElementById('apply');
      if (apply) apply.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  doors.forEach(function (d) {
    d.addEventListener('click', function () { selectTrack(d.getAttribute('data-door'), true); });
  });
  trackRadios.forEach(function (r) {
    r.addEventListener('change', function () { if (r.checked) selectTrack(r.value, false); });
  });

  /* Dedicated persona pages: the section carries data-locked-track and hides
     doors + picker; lock everything to that track on load. */
  var lockedTrack = root.getAttribute('data-locked-track');

  /* ---------- Calculator (role-aware) ----------
     Each role gets its own question, slider range, and rate. Role follows the
     selected door but can also be switched directly via the pills. */
  var calc = root.querySelector('[data-partners-calc]');
  var range = calc && calc.querySelector('[data-calc-range]');
  var rolePills = calc ? Array.prototype.slice.call(calc.querySelectorAll('[data-calc-role]')) : [];
  var ROLE_CFG = {
    player: { label: 'Playing partners you refer each month', max: 20, start: 4 },
    /* Coaches get a rate floor: they start at 10% (top of the standard ladder)
       and reach 12% sooner. A $200/hr coach won't move for pocket change. */
    coach: { label: 'Students you advise each month', max: 60, start: 12, floor: 10 },
    /* Creators start at 8% (Creator Program base, above the standard 5%). */
    creator: { label: 'Followers who buy each month', max: 150, start: 25, floor: 8 },
    /* Community track ('club' role): communities start at 8%, same as creators. */
    club: { label: 'Members who shop each month', max: 300, start: 50, floor: 8 }
  };
  var calcRole = 'player';

  function setCalcRole(role, resetValue) {
    if (!ROLE_CFG[role]) return;
    calcRole = role;
    rolePills.forEach(function (p) {
      var on = p.getAttribute('data-calc-role') === role;
      p.classList.toggle('is-active', on);
      p.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    if (range) {
      range.max = String(ROLE_CFG[role].max);
      if (resetValue) range.value = String(ROLE_CFG[role].start);
      if (parseInt(range.value, 10) > ROLE_CFG[role].max) range.value = String(ROLE_CFG[role].max);
    }
    updateCalc();
  }

  /* Incremental partner rate: grows with monthly referred revenue.
     Thresholds at AOV $250: ~10 orders -> 8%, ~30 -> 10%, ~60 -> 12%. */
  var RATE_TIERS = [
    { min: 15000, rate: 12 },
    { min: 7500, rate: 10 },
    { min: 2500, rate: 8 },
    { min: 0, rate: 5 }
  ];
  function tierRate(revenue) {
    for (var i = 0; i < RATE_TIERS.length; i++) {
      if (revenue >= RATE_TIERS[i].min) return RATE_TIERS[i].rate;
    }
    return 5;
  }

  function updateCalc() {
    if (!calc || !range) return;
    var aov = parseFloat(calc.getAttribute('data-aov')) || 250;
    var cfg = ROLE_CFG[calcRole];
    var n = parseInt(range.value, 10);
    var ctxEl = calc.querySelector('[data-calc-context]');
    if (ctxEl) ctxEl.textContent = cfg.label;
    var countEl = calc.querySelector('[data-calc-count]');
    if (countEl) countEl.textContent = String(n);
    var revenue = n * aov;
    var rate = Math.max(tierRate(revenue), cfg.floor || 0);
    var rateEl = calc.querySelector('[data-calc-rate]');
    if (rateEl) rateEl.textContent = rate + '%';
    var amtEl = calc.querySelector('[data-calc-amount]');
    if (amtEl) amtEl.textContent = '$' + Math.round(revenue * (rate / 100)).toLocaleString('en-US');
  }
  rolePills.forEach(function (p) {
    p.addEventListener('click', function () { setCalcRole(p.getAttribute('data-calc-role'), true); });
  });
  if (range) range.addEventListener('input', updateCalc);
  if (lockedTrack) {
    selectTrack(lockedTrack, false);
  } else {
    setCalcRole('player', true);
  }

  /* ---------- Submit ---------- */
  if (!form || !endpoint) return;

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    var errEl = form.querySelector('[data-form-error]');
    var btn = form.querySelector('[data-form-submit]');
    errEl.hidden = true;

    if (!currentTrack) {
      errEl.textContent = 'Please select who you are applying as.';
      errEl.hidden = false;
      var picker = form.querySelector('.pt-form__tracks');
      if (picker) picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!form.reportValidity()) return;

    var data = { track: currentTrack };
    new FormData(form).forEach(function (v, k) { data[k] = v; });

    /* Editor-defined questions: fold answers into the message field so they land
       in the applicant's UpPromote profile notes without endpoint changes. */
    var extras = [];
    Array.prototype.forEach.call(form.querySelectorAll('[data-question-label]'), function (el) {
      if (!el.disabled && el.value) extras.push(el.getAttribute('data-question-label') + ': ' + el.value);
    });
    if (extras.length) {
      data.message = ((data.message ? data.message + ' | ' : '') + extras.join(' | ')).slice(0, 490);
    }

    /* No password field in the form (friction). UpPromote accounts need one, so
       generate a throwaway — partners set their own via "Forgot password" later. */
    if (!data.password) {
      var chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!#%&*';
      var buf = new Uint8Array(20);
      (window.crypto || window.msCrypto).getRandomValues(buf);
      var pw = '';
      for (var i = 0; i < buf.length; i++) pw += chars[buf[i] % chars.length];
      data.password = pw;
    }

    btn.disabled = true;
    btn.setAttribute('data-label', btn.textContent);
    btn.textContent = 'Submitting…';

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
      .then(function (res) { return res.json().then(function (j) { return { ok: res.ok, body: j }; }); })
      .then(function (r) {
        if (r.ok && r.body && r.body.ok) {
          form.hidden = true;
          var success = root.querySelector('[data-form-success]');
          var copy = success.querySelector('[data-success-copy]');
          if (copy) {
            var successAttr = { club: 'data-success-club', player: 'data-success-player' }[currentTrack] || 'data-success-default';
            copy.textContent = copy.getAttribute(successAttr) || copy.getAttribute('data-success-default');
          }
          success.hidden = false;
          success.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          throw new Error((r.body && r.body.error) || 'Something went wrong. Please try again.');
        }
      })
      .catch(function (err) {
        errEl.textContent = err.message || 'Network error — please try again.';
        errEl.hidden = false;
      })
      .then(function () {
        btn.disabled = false;
        btn.textContent = btn.getAttribute('data-label') || 'Apply now';
      });
  });
})();
